import { DateTime } from '@ksyos/date-time';
import { Alert, Button, Modal, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { getSelectedLanguage } from '@frontend/translate';
import { deactivateEmploymentSchema } from '../../../../common/validation/employment';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { validate } from '../../../utils/validation';

interface IEndEmploymentsModalProps {
    backUrl: string;
    employmentId?: string;
    handleNo: () => void;
    isOpen: boolean;
    practitionerId?: string;
}

export function EndEmploymentsModal(props: IEndEmploymentsModalProps) {
    const [hasActiveActivities, setHasActiveActivities] = useState(false);
    const abortableFetch = useAbortableFetch();

    const translate = getSelectedLanguage();

    useEffect(() => {
        getHasActiveActivities();
    }, [props.isOpen]);

    let extraInformationAlert;
    if (hasActiveActivities) {
        if (props.employmentId) {
            extraInformationAlert = <Alert appearance="warning">{translate.employment.mayHaveActiveActivities}</Alert>;
        } else {
            extraInformationAlert = (
                <Alert appearance="warning">{translate.employment.mayHaveActiveActivitiesForCareProvider}</Alert>
            );
        }
    } else {
        extraInformationAlert = <Alert appearance="info">{translate.employment.dontHaveActiveActivities}</Alert>;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => props.handleNo()}
            size="medium"
        >
            <Modal.Header>{translate.employment.endAll}</Modal.Header>
            <Modal.CloseButton></Modal.CloseButton>
            <Modal.Body>
                {extraInformationAlert}
                <Text>
                    {props.employmentId
                        ? translate.employment.endEmploymentModalBody
                        : translate.employment.endAllEmploymentsModalBody}
                </Text>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.handleNo()}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button onClick={async () => await handleEndEmployments()}>{translate.common.terminate}</Button>
            </Modal.Footer>
        </Modal>
    );

    async function handleEndEmployments() {
        let activeEmployments;
        if (props.employmentId) {
            activeEmployments = [
                (await abortableFetch.fetchJson(`/api/employments/${props.employmentId}`, 'GET')).json,
            ];
        } else {
            activeEmployments = (
                await abortableFetch.fetchJson(`/api/user/employments?practitionerId=${props.practitionerId}`, 'GET')
            ).json.items;
        }
        for (const activeEmployment of activeEmployments) {
            const deactivateEmployment = {
                endDate: DateTime.now().format('YYYY-MM-DD'),
                startDate: activeEmployment.startDate,
            };

            const { errors: validationErrors, item: deactivateEmploymentValidated } = validate(
                deactivateEmploymentSchema,
                deactivateEmployment
            );
            if (validationErrors.size > 0) {
                throw new Error('Incorrect employment start or end date.');
            } else if (deactivateEmploymentValidated) {
                const url = `/api/employments/${activeEmployment.id}/deactivate`;
                await abortableFetch.fetchJson(url, 'POST', deactivateEmploymentValidated);
            }
        }
        window.location.href = props.backUrl;
    }

    async function getHasActiveActivities() {
        let url = '/api/requests/active?';
        if (props.employmentId) {
            url += `employmentId=${props.employmentId}`;
        } else {
            url += `practitionerId=${props.practitionerId}`;
        }

        const result = await abortableFetch.fetchJson(url, 'GET');
        setHasActiveActivities(result.json.hasActiveActivities);
    }
}
